<script lang="ts">
  /**
   * AboutOverlay - Modal overlay with information about the app
   * Explains the purpose and credits for the application
   */

    // Props
  interface Props {
    visible?: boolean;
    onClose?: () => void;
  }

  let {
    visible = false,
    onClose = () => {
    },
  }: Props = $props();

  // Handle backdrop click
  function handleBackdropClick(e: MouseEvent) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }
</script>

{#if visible}
  <div
    class="overlay-backdrop"
    onclick={handleBackdropClick}
    role="dialog"
    aria-modal="true"
    aria-labelledby="about-title"
  >
    <div class="about-panel">
      <h2 id="about-title" class="title">About This App</h2>

      <div class="about-content">
        <div class="content-section">
          <p class="description">
            <span class="icon">📚</span>
            This is a <strong>free educational tool</strong> created by a father for his son Mathias,
            to help learn letter sounds and phonics in a less-boring, interactive way.
          </p>
        </div>

        <div class="content-section">
          <p class="description">
            <span class="icon">🎮</span>
            Practice letter sounds by playing as a letter or challenge yourself
            in monster mode to build complete words!
          </p>
        </div>

        <div class="content-section">
          <p class="attribution">
            <span class="icon">🤖</span>
            Built with the assistance of <strong>Claude Code</strong>, for code, Gemini 2.5 for sounds and the patience
            of my family.
          </p>
        </div>

        <div class="footer-note">
          <p>Made with ❤️ for educational purposes only</p>
          <p class="version">Free to use • No ads • No data collection • <a
            href="https://creativecommons.org/licenses/by-nc-sa/4.0/deed.en" target="_blank">CC BY-NC-SA</a></p>
        </div>
      </div>

      <!-- Close Button -->
      <button class="close-button" onclick={onClose} aria-label="Close about">
        ✕ Close
      </button>
    </div>
  </div>
{/if}

<style>
    .overlay-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        backdrop-filter: blur(4px);
        animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .about-panel {
        background: var(--color-space-blue);
        border-radius: var(--radius-lg);
        padding: var(--space-xl);
        width: 90%;
        max-width: 500px;
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
        animation: slideUp 0.3s ease;
    }

    @keyframes slideUp {
        from {
            transform: translateY(20px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }

    .title {
        font-size: var(--font-heading);
        font-weight: bold;
        color: var(--color-bright-yellow);
        margin: 0 0 var(--space-lg) 0;
        text-align: center;
    }

    .about-content {
        display: flex;
        flex-direction: column;
        gap: var(--space-lg);
        margin-bottom: var(--space-xl);
    }

    .content-section {
        background: rgba(0, 0, 0, 0.2);
        border-radius: var(--radius-md);
        padding: var(--space-md);
    }

    .description,
    .attribution {
        font-size: var(--font-body);
        color: var(--color-text-primary);
        line-height: 1.6;
        margin: 0;
    }

    .description strong,
    .attribution strong {
        color: var(--color-bright-yellow);
    }

    .icon {
        font-size: 24px;
        display: inline-block;
        margin-right: var(--space-sm);
        vertical-align: middle;
    }

    .footer-note {
        text-align: center;
        padding-top: var(--space-md);
        border-top: 2px solid rgba(255, 255, 255, 0.1);
    }

    .footer-note p {
        margin: var(--space-xs) 0;
        font-size: var(--font-body);
        color: var(--color-text-secondary);
    }

    .version {
        font-size: 14px;
        color: var(--color-text-muted);
        font-style: italic;
    }

    /* Close Button */
    .close-button {
        width: 100%;
        padding: var(--space-md);
        border: none;
        border-radius: var(--radius-md);
        background: var(--color-cosmic-purple);
        color: white;
        font-size: var(--font-lg);
        font-weight: bold;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .close-button:hover {
        background: var(--color-bright-yellow);
        color: var(--color-space-deep);
        transform: scale(1.05);
    }

    .close-button:active {
        transform: scale(0.98);
    }

    /* Responsive Design */
    @media (max-width: 767px) {
        .about-panel {
            width: 95%;
            padding: var(--space-lg);
        }

        .title {
            font-size: 24px;
        }

        .description,
        .attribution {
            font-size: 16px;
        }

        .icon {
            font-size: 20px;
        }
    }
</style>
